import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import FormContainer from '../../components/FormContainer';
import Meta from '../../components/Meta';
import Loader from '../../components/Loader';
import { useCheckDeliveryPartnerExistsMutation, useRegisterDeliveryPartnerMutation } from '../../slices/usersApiSlice';
import { setCredentials } from '../../slices/authSlice';
import { toast } from 'react-toastify';
import { auth } from '../../firebase';
import { signInWithPhoneNumber } from 'firebase/auth';
import { setupRecaptchaVerifier, clearRecaptcha } from '../../hooks/useRecaptchaVerifier';
import initFirebaseAppCheck from '../../utils/initFirebaseAppCheck';
import DeliveryPartnerTermsModal from '../../components/DeliveryPartnerTermsModal';

const DeliveryPartnerRegisterScreen = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const confirmationResultRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [checkDeliveryPartnerExists] = useCheckDeliveryPartnerExistsMutation();
  const [registerDeliveryPartner, { isLoading }] = useRegisterDeliveryPartnerMutation();

  const { userInfo } = useSelector((state) => state.auth);
  const { search } = useLocation();
  const redirect = new URLSearchParams(search).get('redirect') || '/deliverypartner';

  const [showTerms, setShowTerms] = useState(false);

  if (process.env.NODE_ENV !== 'development'){
    initFirebaseAppCheck();
  }

  useEffect(() => {
    if (userInfo) {
      navigate(redirect);
    }
  }, [userInfo, redirect, navigate]);

  useEffect(() => {
    return () => {
      clearRecaptcha();
      confirmationResultRef.current = null;
    };
  }, []);

  const onSignInSubmit = async () => {
    const containerId = 'recaptcha-container';
    const formattedPhone = phone.startsWith('+') ? phone : `+91${phone}`;

    try {
      const { exists } = await checkDeliveryPartnerExists({ phone }).unwrap();
      if (exists) {
        toast.error("Delivery partner already exists. Please login instead.", { autoClose: 2000 });
        return;
      }

      setSendingOtp(true);

      await new Promise((res) => requestAnimationFrame(res));
      const verifier = await setupRecaptchaVerifier(auth, containerId);

      const result = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      confirmationResultRef.current = result;

      toast.success('OTP has been sent', { autoClose: 1000 });
    } catch (error) {
      console.error('Failed to send OTP:', error);
      toast.error('Could not send OTP. Please try again', { autoClose: 2000 });
      setTimeout(() => window.location.reload(), 2000);
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    const confirmationResult = confirmationResultRef.current;

    try {
      setVerifyingOtp(true);
      if (!confirmationResult) {
        throw new Error('OTP session not initialized. Please resend OTP.');
      }

      console.log('Attempting OTP verify with:', otp);
      const result = await confirmationResult.confirm(otp);
      const token = await result.user.getIdToken(true);

      console.log('Firebase token:', token.slice(0, 20) + '...');

      const res = await registerDeliveryPartner({ token, name, email }).unwrap();
      dispatch(setCredentials({ ...res }));
      navigate(redirect);
    } catch (error) {
      console.error('OTP verification failed:', error);
      toast.error(error.message || 'Invalid OTP. Please try again', { autoClose: 2000 });
    } finally {
      setVerifyingOtp(false);
    }
  };

  return (
    <FormContainer>
      <Meta />
      <h1>Delivery Partner Registration</h1>
      <Form autoComplete="off">
        <Form.Group controlId="name" className="my-3">
          <Form.Label>
            Name <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </Form.Group>

        <Form.Group controlId="email" className="my-3">
          <Form.Label>Email Address</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </Form.Group>

        <Form.Group controlId="phone" className="my-3">
          <Form.Label>
            Phone <span className="text-danger">*</span>
          </Form.Label>
          <Row>
            <Col>
              <Form.Control
                type="tel"
                placeholder="10 digit India phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </Col>
            <Col>
              <Button
                onClick={onSignInSubmit}
                disabled={isLoading || !phone || sendingOtp}
              >
                {sendingOtp ? (
                  <>
                    <Spinner animation="border" size="sm" /> Sending...
                  </>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </Col>
          </Row>
        </Form.Group>
        <Form.Group>
          <Form.Label>
            <strong>
              <span className="text-danger">
                Make sure to check for OTP in your Spam & Blocked SMS as well
              </span>
            </strong>
          </Form.Label>
        </Form.Group>
        <Form.Group controlId="otp" className="my-3">
          <Form.Label>
            OTP <span className="text-danger">*</span>
          </Form.Label>
          <Row>
            <Col>
              <Form.Control
                type="number"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </Col>
            <Col>
              <Button
                onClick={verifyOtp}
                disabled={isLoading || !otp || verifyingOtp}
              >
                {verifyingOtp ? (
                  <>
                    <Spinner animation="border" size="sm" /> Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </Button>
            </Col>
          </Row>
        </Form.Group>

        {isLoading && <Loader />}
      </Form>

      <div id="recaptcha-container" />
      
      <div className="mt-2">
        <p>
          By continuing to use the website, you agree to our{" "}
          <span
            className="terms-text-link"
            onClick={() => setShowTerms(true)}
            role="button"
            tabIndex={0}
          >
            Terms & Conditions
          </span>
        </p>
      </div>
      <DeliveryPartnerTermsModal show={showTerms} onHide={() => setShowTerms(false)} />

      <Row className="mt-2">
        <Col>
          Already registered?{' '}
          <Link
            to={
              redirect
                ? `/deliverypartner/login?redirect=${redirect}`
                : '/deliverypartner/login'
            }
          >
            Login
          </Link>
        </Col>
      </Row>
    </FormContainer>
  );
};

export default DeliveryPartnerRegisterScreen;